import { useInfiniteQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/button.js";
import { Card } from "../../components/ui/card.js";
import { CreateModal } from "../create/index.js";
import { api } from "../../lib/api.js";
import { WordFilters } from "./WordFilters.js";
import { WordModal } from "./WordModal.js";
import { buildEditDraft, type EditDraft, type WordFilterDraft } from "./types.js";

export function WordsPage() {
  const { t } = useTranslation();
  const limit = 20;
  const [applied, setApplied] = useState<WordFilterDraft>({});
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState<EditDraft | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isSyncingFailed, setIsSyncingFailed] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const query = useInfiniteQuery({
    queryKey: ["vocab", "list", applied, limit],
    queryFn: ({ pageParam }) =>
      api.listVocab({
        ...applied,
        cursor: typeof pageParam === "string" ? pageParam : undefined,
        limit,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.has_more ? (lastPage.next_cursor ?? undefined) : undefined),
  });

  const items = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data]);
  const hasMore = query.hasNextPage;
  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedId) ?? null,
    [items, selectedId],
  );

  useEffect(() => {
    if (!selectedItem) {
      setEditDraft(null);
      setIsEditMode(false);
      return;
    }
    if (!isEditMode) {
      setEditDraft(buildEditDraft(selectedItem));
    }
  }, [selectedItem, isEditMode]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting && hasMore && !query.isFetchingNextPage) {
          void query.fetchNextPage();
        }
      },
      { rootMargin: "200px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, query]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setSelectedId(null);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  function openRow(itemId: string) {
    const item = items.find((i) => i.id === itemId);
    if (!item) return;
    setSelectedId(itemId);
    setEditDraft(buildEditDraft(item));
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-[var(--text-primary)]">
            {t("words.title")}
          </h1>
          <p className="mt-1 text-[var(--text-secondary)]">{t("words.subtitle")}</p>
        </div>
        <Button variant="primary" onClick={() => setIsCreateOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          {t("words.addWord")}
        </Button>
      </div>

      <WordFilters onSearch={setApplied} />

      {query.isPending ? <Card>{t("words.loading")}</Card> : null}
      {query.isError ? <Card className="text-rose-300">{t("words.error")}</Card> : null}
      {items.length === 0 && !query.isPending ? <Card>{t("words.empty")}</Card> : null}

      {items.length > 0 ? (
        <Card className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-[var(--glass-border)] text-left text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                <th className="py-2 pr-4">{t("words.columnWord")}</th>
                <th className="py-2 pr-4">{t("words.columnMeaning")}</th>
                <th className="py-2 pr-4">{t("words.columnTopic")}</th>
                <th className="py-2 pr-4">{t("words.columnCreated")}</th>
                <th className="py-2 pr-4">{t("words.columnSync")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const primaryTarget = item.targets[0];
                return (
                  <tr
                    key={item.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openRow(item.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") openRow(item.id);
                    }}
                    className="cursor-pointer border-b border-[var(--glass-border)]/50 transition-colors last:border-0 hover:bg-white/5"
                  >
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[var(--text-primary)]">{item.word}</span>
                        <span className="rounded-full bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-[var(--text-muted)]">
                          {item.data.cefr_level}
                        </span>
                      </div>
                    </td>
                    <td className="max-w-xs truncate py-3 pr-4 text-[var(--text-secondary)]">
                      {item.meaning}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-wrap gap-1.5">
                        {item.data.topics.map((topic) => (
                          <span
                            key={topic}
                            className="whitespace-nowrap rounded-full bg-[var(--primary)]/10 px-2 py-0.5 text-xs font-medium text-[var(--accent)]"
                          >
                            {t(`topic.${topic}`)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="whitespace-nowrap py-3 pr-4 text-xs text-[var(--text-muted)]">
                      {new Date(item.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 pr-4">
                      {primaryTarget ? (
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-[var(--text-secondary)]">
                          <span
                            className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                              primaryTarget.status === "synced"
                                ? "bg-[var(--success)]"
                                : primaryTarget.status === "failed"
                                  ? "bg-[var(--danger)]"
                                  : "bg-[var(--warning)]"
                            }`}
                            aria-hidden
                          />
                          {t(`syncStatus.${primaryTarget.status}`)}
                        </span>
                      ) : (
                        <span className="text-xs text-[var(--text-muted)]">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      ) : null}

      <div ref={loadMoreRef} className="flex items-center justify-center py-4">
        {query.isFetchingNextPage ? (
          <p className="text-sm text-[var(--text-muted)]">{t("words.loadingMore")}</p>
        ) : hasMore ? (
          <Button variant="secondary" onClick={() => void query.fetchNextPage()}>
            {t("words.loadMore")}
          </Button>
        ) : items.length > 0 ? (
          <p className="text-sm text-[var(--text-muted)]">{t("words.endOfList")}</p>
        ) : null}
      </div>

      {selectedItem ? (
        <WordModal
          item={selectedItem}
          editDraft={editDraft}
          isEditMode={isEditMode}
          isSavingEdit={isSavingEdit}
          isSyncingFailed={isSyncingFailed}
          onClose={() => setSelectedId(null)}
          onSetEditMode={setIsEditMode}
          onSetEditDraft={setEditDraft}
          onAfterMutation={async () => {
            await query.refetch();
          }}
          setIsSavingEdit={setIsSavingEdit}
          setIsSyncingFailed={setIsSyncingFailed}
        />
      ) : null}

      {isCreateOpen ? (
        <CreateModal
          onClose={() => setIsCreateOpen(false)}
          onCreated={() => void query.refetch()}
        />
      ) : null}
    </div>
  );
}
